/**
 * The results grid's column-header "Add to Filter" in a Query tab — Table
 * Explorer's header item, where the filter is the Visual Query Editor's WHERE
 * condition editor: which statement the results came from, and why the item is
 * disabled for a column the editor has no condition for.
 *
 * The column is placed in the statement the way the cell's "Add to WHERE"
 * places it (`resultColumnWhereField`), fed the document's own schema snapshot
 * (`getVqeSchemaSnapshotForDocument`) when one has landed, so a column only
 * the snapshot can place is caught here too instead of staying enabled and
 * failing once the editor opens. With no snapshot yet, unplaceable columns are
 * still let through (today's behaviour) — the editor decides once it has one.
 * The item is enabled only where a condition editor takes the column: the
 * view's (in the SQL view the compact editor, which shows a summary with no
 * WHERE row for what `classifyCompactDegrade` rejects), and a column of a
 * table — the editor lists no derived table's, CTE's or table function's
 * columns.
 */

import type { DatabaseEngineId } from '../../../../core/engines';
import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../../sql-resolver/scope/identifiers';
import { bindingKeyOf, querySources, type QuerySource } from '../../../sql-resolver/scope/querySources';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { ParsedBuffer, QueryStructure, SchemaSnapshot } from '../../../sql-resolver/types';
import { classifyCompactDegrade, type CompactDegradeReason } from '../../visual-query-editor/compact/compactDegrade';
import { detectRenderMode } from '../../visual-query-editor/detection/detectRenderMode';
import { vqeCopy } from '../../visual-query-editor/ux/vqeCopy';
import { classifyTabQuery, vqeParseOptionsFor } from '../../visual-query-editor/workspace/classifyTabQuery';
import { nonRelationSourcesExposing, sameName } from './resultColumnSource';
import { resultColumnWhereField, type ResultColumnTarget } from './resultColumnWhereField';

/** The Query tab's "Add to Filter", as the results grid calls it. */
export type AddColumnToFilter = {
  /** Why the column gets no condition (the item shows disabled with it), or null. */
  readonly unavailableReason: (column: ResultColumnTarget) => string | null;
  readonly open: (column: ResultColumnTarget) => void;
};

/** A statement of the tab's buffer, with its render mode. */
export type ResultStatement = {
  readonly buffer: ParsedBuffer;
  readonly statement: StatementInfo;
  readonly isSelect: boolean;
  /** Its first offset, and the offset after its last token (before a `;`). */
  readonly start: number;
  readonly end: number;
};

/**
 * The statement at `at.offset` (the Visual view's shown statement), else the
 * one "Add to WHERE" edits in the SQL view: the last statement of the last
 * partial run while the buffer still holds that text, else of the buffer.
 */
export function resultStatement(
  text: string,
  engineId: DatabaseEngineId,
  at: { readonly offset: number | null } | { readonly partialSql: string | null },
): ResultStatement | null {
  if (!text.trim()) return null;
  try {
    const classified = classifyTabQuery(text, vqeParseOptionsFor(engineId), 'offset' in at ? (at.offset ?? undefined) : undefined);
    const { buffer } = classified;
    let statement = classified.statement;
    if ('partialSql' in at) {
      const partialIdx = at.partialSql ? text.indexOf(at.partialSql) : -1;
      const from = partialIdx >= 0 ? partialIdx : 0;
      const to = partialIdx >= 0 ? partialIdx + at.partialSql!.length : text.length;
      const inRange = buffer.documentStructure.statements.filter((s) => s.range.startOffset >= from && s.range.startOffset < to);
      statement = inRange[inRange.length - 1] ?? null;
    }
    if (!statement) return null;
    const start = statement.range.startOffset;
    const mode = detectRenderMode(buffer, { offset: start, line: 0, column: 0 });
    const end = start + text.slice(start, statement.range.endOffset).replace(/[\s;]+$/, '').length;
    return { buffer, statement, isSelect: mode === 'select', start, end };
  } catch {
    return null;
  }
}

/** What the compact editor shows no WHERE row for, as the item's reason says it. */
const COMPACT_SHAPE: Partial<Record<CompactDegradeReason, string>> = {
  'window-function': 'a SELECT with a named window',
  subquery: 'a SELECT with a derived table in FROM',
};

const SOURCE_KIND: Record<Exclude<QuerySource['kind'], 'relation'>, string> = {
  derived: vqeCopy.picker.sourceKindDerived,
  cte: vqeCopy.picker.sourceKindCte,
  function: vqeCopy.picker.sourceKindFunction,
};

/**
 * Why "Add to Filter" opens no condition for `column` of `result` in the tab's
 * `view`, or null. `snapshot` — the condition editor's own schema snapshot for
 * this document (`getVqeSchemaSnapshotForDocument`), when one has landed —
 * lets a `*` mixing a base table with a derived table / CTE / table function
 * expand fully, so a column only the snapshot can place (e.g. a derived
 * table's own column under a `SELECT *` joined to a real table) is caught here
 * the same way the editor itself would reject it. With no snapshot yet this
 * falls back to the snapshot-free checks below, same as before.
 */
export function columnFilterUnavailableReason(
  result: ResultStatement | null,
  column: ResultColumnTarget,
  engineId: DatabaseEngineId,
  view: 'sql' | 'visual',
  snapshot?: SchemaSnapshot | null,
): string | null {
  if (!result?.isSelect) return 'These results are not from a SELECT the Visual Query Editor can edit.';
  let structure;
  try {
    structure = extractQueryStructure(result.buffer, result.statement);
  } catch {
    return null; // The condition editor reads the statement itself and says what went wrong.
  }
  if (structure.compoundBranches?.length) return 'A UNION, INTERSECT or EXCEPT result has no single WHERE to filter it.';
  const options = vqeParseOptionsFor(engineId);
  const field = resultColumnWhereField(structure, options, column, snapshot);
  if (field.kind === 'unfilterable') return `“${column.name}” is computed by an aggregate or window function, which WHERE can't filter.`;
  if (field.kind === 'field' && field.computed) return `“${column.name}” is computed by an expression, not read from a table column.`;
  const source = field.kind === 'field' ? fieldSource(structure, options, field.sql) : undefined;
  if (source && source.kind !== 'relation') {
    return `“${column.name}” comes from the ${SOURCE_KIND[source.kind]} “${source.exposedName}”, whose columns the condition editor doesn't list.`;
  }
  const compact = view === 'sql' ? classifyCompactDegrade('select', structure, options.engine) : null;
  if (compact?.degraded) {
    return `The compact editor can't edit ${COMPACT_SHAPE[compact.reason] ?? 'this statement'}. Use Add to Filter in the Visual view.`;
  }
  return null;
}

/**
 * The FROM source a column's WHERE field reads: the one its qualifier names,
 * else the statement's only one, else — for an unqualified reference over
 * several sources — the one derived table / CTE / table function whose known
 * column list is the only one holding that name (a base table can't be
 * checked without the schema snapshot, so it never disables the item here).
 */
function fieldSource(structure: QueryStructure, options: ParseOptions, sql: string): QuerySource | undefined {
  const sources = querySources(structure, options.engine);
  const ref = readQualifiedName(sql, 0, sql.length, options);
  if (!ref || ref.range.endOffset !== sql.length) return undefined;
  const qualifier = ref.qualified.foldedSchema;
  if (qualifier === undefined) {
    if (sources.length === 1) return sources[0];
    // A USING / NATURAL merge key is genuinely reachable through its relation
    // side too (a base table's columns are unknown here), so a name a join
    // merges is never narrowed to a derived / CTE / function source.
    if (isJoinMergeKey(structure, ref.qualified.name)) return undefined;
    const holders = nonRelationSourcesExposing(structure, options, ref.qualified.name);
    return holders.length === 1 ? holders[0] : undefined;
  }
  const key = bindingKeyOf(options.engine, qualifier);
  return sources.find((s) => s.bindingKey === key);
}

/** Whether `name` is a join's USING key, or any join is NATURAL (its merge keys are unknown without a snapshot). */
function isJoinMergeKey(structure: QueryStructure, name: string): boolean {
  const joins = structure.joins ?? [];
  return joins.some((j) => j.natural || j.using?.some((u) => sameName(u, name)));
}
